import LoadingSpinner from "../shared-components/LoadingSpinner";
import type { ListCompareResult } from "../360/ThreeSixtyCompare";

export type CompareProgress = {
  phase: "v2025" | "v2026";
  v2025Loaded: number;
  v2026Loaded: number;
  v2025ReportedTotal: number | null;
  v2026ReportedTotal: number | null;
};

export type OnComparePageProgress = (
  loaded: number,
  reportedTotal: number | null
) => void;

function formatLoadedCount(
  loaded: number,
  reportedTotal: number | null
): string {
  if (reportedTotal != null) {
    return `${loaded} / ${reportedTotal}`;
  }
  return String(loaded);
}

function CompareCountLine({
  label,
  loaded,
  reportedTotal,
  loading,
  error,
  match,
}: {
  label: string;
  loaded: number;
  reportedTotal: number | null;
  loading: boolean;
  error?: boolean;
  match?: boolean;
}) {
  const countClass =
    match === undefined
      ? "text-gray-700 font-mono"
      : match
      ? "text-green-700 font-mono"
      : "text-red-700 font-mono";

  return (
    <div className="flex items-center gap-1">
      <span>{label}:</span>
      {loading ? (
        <LoadingSpinner className="!w-4 !h-4 !relative !top-auto !left-auto" />
      ) : null}
      <span className={error ? "text-gray-400 font-mono" : countClass}>
        {error ? "—" : formatLoadedCount(loaded, reportedTotal)}
      </span>
    </div>
  );
}

export function CompareCountsCell({
  progress,
  result,
  running,
}: {
  progress: CompareProgress | null;
  result: ListCompareResult | null;
  running: boolean;
}) {
  if (running && progress) {
    const countsMatch =
      progress.phase === "v2026" &&
      progress.v2025Loaded === progress.v2026Loaded;
    return (
      <>
        <CompareCountLine
          label="v2025"
          loaded={progress.v2025Loaded}
          reportedTotal={progress.v2025ReportedTotal}
          loading={progress.phase === "v2025"}
          match={progress.phase === "v2026" ? countsMatch : undefined}
        />
        <CompareCountLine
          label="v2026"
          loaded={progress.v2026Loaded}
          reportedTotal={progress.v2026ReportedTotal}
          loading={progress.phase === "v2026"}
          match={progress.phase === "v2026" ? countsMatch : undefined}
        />
      </>
    );
  }

  if (result) {
    const countsMatch = result.v2025Count === result.v2026Count;
    return (
      <>
        <CompareCountLine
          label="v2025"
          loaded={result.v2025Count ?? 0}
          reportedTotal={null}
          loading={false}
          error={Boolean(result.v2025Error)}
          match={countsMatch}
        />
        <CompareCountLine
          label="v2026"
          loaded={result.v2026Count ?? 0}
          reportedTotal={null}
          loading={false}
          error={Boolean(result.v2026Error)}
          match={countsMatch}
        />
      </>
    );
  }

  return <span className="text-gray-400 text-xs">—</span>;
}
